import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Heart, Shield, Users, Eye, Target } from 'lucide-react';
import { siteConfig } from '../data/mock';
import SEO from '../components/SEO';

const AboutPage = () => {
  return (
    <main>
      <SEO 
        title="About Us"
        description="Learn about The Grazing Trust, a Nonprofit Association working towards SCIO status. Our mission is to provide compassionate care, rehabilitation, and secure futures for horses in need across Scotland."
        keywords="about The Grazing Trust, Scottish horse charity, equine welfare organisation, horse sanctuary mission, natural living philosophy"
        canonical="https://thegrazingtrust.org/about"
      />
      {/* Page Header */}
      <div className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight size={16} />
            <span>About</span>
          </div>
          <h1 className="display-medium">About The Grazing Trust</h1>
          <p className="body-large">
            A Nonprofit Association working to achieve full SCIO status, dedicated to equine welfare across Scotland.
          </p>
        </div>
      </div>

      {/* Our Story */}
      <section className="section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'center' }}>
            <div>
              <h2 className="heading-1" style={{ marginBottom: '24px' }}>Our Story</h2>
              <p className="body-medium" style={{ marginBottom: '16px' }}>
                The Grazing Trust was founded with a simple but powerful mission: to provide a safe haven for horses in need across Scotland. We believe every horse deserves compassionate care, dignity, and a secure future.
              </p>
              <p className="body-medium" style={{ marginBottom: '16px' }}>
                We understand that circumstances change. Financial difficulties, health challenges, or life transitions can make it impossible for devoted owners to continue caring for their beloved horses. That’s where we step in.
              </p>
              <p className="body-medium">
                Our sanctuary operates on a natural-living philosophy, ensuring horses can live as nature intended – in herds, with ample space to graze, socialise, and simply be horses.
              </p>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1505494978186-6cde6324454b?w=800&auto=format&fit=crop"
                alt="Herd of horses in a natural pasture setting"
                style={{ width: '100%', borderRadius: '24px', boxShadow: '0 8px 32px rgba(0,69,52,0.15)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Our Values</h2>
            <p className="body-medium">The principles that guide everything we do.</p>
          </div>
          <div className="grid-3">
            {[
              {
                icon: Heart,
                title: 'Compassion',
                description: 'We treat every horse and owner with kindness, understanding that asking for help is never easy.'
              },
              {
                icon: Shield,
                title: 'Welfare First',
                description: 'The physical and mental wellbeing of horses is at the heart of every decision we make.'
              },
              {
                icon: Eye,
                title: 'Transparency',
                description: 'We are open about our work, our finances, and our approach to equine care.'
              },
              {
                icon: Users,
                title: 'Community',
                description: 'We believe in working together – with owners, volunteers, and the wider equine community.'
              },
              {
                icon: Target,
                title: 'Natural Living',
                description: 'Horses thrive when they can live naturally, in herds with space to roam and graze.'
              },
              {
                icon: Shield,
                title: 'Responsibility',
                description: 'Every horse in our care receives a lifetime commitment to their wellbeing.'
              }
            ].map((value, index) => (
              <div key={index} className="network-card">
                <div 
                  style={{ 
                    width: '48px', 
                    height: '48px', 
                    background: 'var(--brand-primary)', 
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}
                >
                  <value.icon size={24} color="var(--brand-dark)" />
                </div>
                <h3 className="heading-3" style={{ marginBottom: '8px' }}>{value.title}</h3>
                <p className="body-medium">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Governance */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Our Commitment</h2>
            <p className="body-medium">
              The Grazing Trust operates with full transparency and is working towards achieving Scottish Charitable Incorporated Organisation (SCIO) status.
            </p>
          </div>
          <div 
            style={{ 
              textAlign: 'center', 
              maxWidth: '800px',
              margin: '0 auto',
              padding: '48px 32px', 
              background: 'white', 
              borderRadius: '16px',
              border: '1px solid var(--border-medium)'
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <Heart size={48} color="var(--brand-dark)" style={{ margin: '0 auto' }} />
            </div>
            <h3 className="heading-2" style={{ marginBottom: '16px' }}>Legal Status</h3>
            <p className="body-medium" style={{ marginBottom: '24px' }}>
              The Grazing Trust is a <strong>Nonprofit Association</strong> currently working to achieve full Scottish Charitable Incorporated Organisation (SCIO) status with OSCR.
            </p>
            <p className="body-small" style={{ color: 'var(--text-light)' }}>
              We are committed to operating with the highest standards of governance, transparency, and accountability as we progress through the charity registration process.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2 className="display-small">Join Us in Making a Difference</h2>
          <p className="body-large" style={{ maxWidth: '600px', margin: '0 auto 32px' }}>
            Whether you donate, volunteer, or help spread the word, you can be part of our mission.
          </p>
          <div className="cta-buttons">
            <Link to="/get-involved" className="btn-cta">Get Involved</Link>
            <Link to="/contact" className="btn-secondary" style={{ color: 'white', borderColor: 'white' }}>Contact Us</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
